import type {
  ModifierGroup,
  ModifierOption,
  ModifierOptionGroupRule,
  ProductModifierConfig,
} from '@/types'

/**
 * Variants setup (F-MOD-N12): the owner-facing shape of a modifier group.
 *
 * The schema stores two orthogonal columns (selection_type × price_mode) plus
 * min/max counts. Owners think in three kinds and a sentence:
 *
 *   size  — "comes in sizes"; each choice IS the item price; exactly one.
 *   one   — "pick one"; a choice may add to the price.
 *   many  — "extras"; any number within a limit; each adds to the price.
 *
 * Everything here is pure so the product form, the shared-lists page and the
 * matrix all project the same way, and the tests can pin the mapping.
 */

export type VariantKind = 'size' | 'one' | 'many'

/** How many the cashier must or may pick, as the sentence builder says it. */
export type RuleMode = 'optional' | 'required' | 'any' | 'upto' | 'atleast' | 'exactly' | 'between'

export interface VariantRule {
  mode: RuleMode
  /** Lower bound for atleast / exactly / between. */
  n: number
  /** Upper bound for upto / exactly / between; null = unlimited. */
  m: number | null
}

export interface ChoiceDraft {
  /** Absent on a row typed in since the last save. */
  id?: string
  name: string
  price: number
  isDefault: boolean
  isAvailable: boolean
  groupRules: ModifierOptionGroupRule[]
}

export interface VariantDraft {
  id?: string
  name: string
  prompt: string
  kind: VariantKind
  rule: VariantRule
  allowQuantity: boolean
  maxPerOption: number
  isActive: boolean
  sortOrder: number
  choices: ChoiceDraft[]
}

export function kindOf(g: Pick<ModifierGroup, 'selection_type' | 'price_mode'>): VariantKind {
  if (g.price_mode === 'replace') return 'size'
  return g.selection_type === 'single' ? 'one' : 'many'
}

export function isSizeGroup(g: Pick<ModifierGroup, 'price_mode'>): boolean {
  return g.price_mode === 'replace'
}

export function ruleOf(g: Pick<ModifierGroup, 'selection_type' | 'price_mode' | 'min_select' | 'max_select'>): VariantRule {
  const kind = kindOf(g)
  if (kind === 'size') return { mode: 'exactly', n: 1, m: 1 }
  if (kind === 'one') return g.min_select >= 1 ? { mode: 'required', n: 1, m: 1 } : { mode: 'optional', n: 0, m: 1 }
  const min = g.min_select
  const max = g.max_select
  if (min === 0 && max == null) return { mode: 'any', n: 0, m: null }
  if (min === 0) return { mode: 'upto', n: 0, m: max }
  if (max == null) return { mode: 'atleast', n: min, m: null }
  if (min === max) return { mode: 'exactly', n: min, m: max }
  return { mode: 'between', n: min, m: max }
}

/** The default rule when the kind changes, so the sentence always makes sense. */
export function defaultRule(kind: VariantKind): VariantRule {
  if (kind === 'size') return { mode: 'exactly', n: 1, m: 1 }
  if (kind === 'one') return { mode: 'required', n: 1, m: 1 }
  return { mode: 'upto', n: 0, m: 3 }
}

/** Switch a rule's mode, keeping whatever numbers still apply. */
export function withMode(rule: VariantRule, mode: RuleMode): VariantRule {
  switch (mode) {
    case 'optional': return { mode, n: 0, m: 1 }
    case 'required': return { mode, n: 1, m: 1 }
    case 'any': return { mode, n: 0, m: null }
    case 'upto': return { mode, n: 0, m: Math.max(1, rule.m ?? 3) }
    case 'atleast': return { mode, n: Math.max(1, rule.n || 1), m: null }
    case 'exactly': { const n = Math.max(1, rule.n || 1); return { mode, n, m: n } }
    case 'between': {
      const n = Math.max(1, rule.n || 1)
      return { mode, n, m: Math.max(n + 1, rule.m ?? n + 2) }
    }
  }
}

/** Modes a kind can take, in the order the sentence dropdown lists them. */
export function modesFor(kind: VariantKind): Array<{ mode: RuleMode; label: string }> {
  if (kind === 'size') return [{ mode: 'exactly', label: 'must pick exactly 1' }]
  if (kind === 'one') {
    return [
      { mode: 'required', label: 'must pick one' },
      { mode: 'optional', label: 'can pick one, or none' },
    ]
  }
  return [
    { mode: 'any', label: 'can pick any number' },
    { mode: 'upto', label: 'can pick up to' },
    { mode: 'atleast', label: 'must pick at least' },
    { mode: 'exactly', label: 'must pick exactly' },
    { mode: 'between', label: 'must pick between' },
  ]
}

/** True when the cashier cannot add the item without answering. */
export function isRequired(rule: VariantRule): boolean {
  return rule.n >= 1
}

/** The short pill text: "pick 1", "up to 3", "optional". */
export function rulePill(kind: VariantKind, rule: VariantRule): string {
  if (kind === 'size') return 'pick 1'
  switch (rule.mode) {
    case 'optional': return 'optional'
    case 'required': return 'pick 1'
    case 'any': return 'any number'
    case 'upto': return `up to ${rule.m}`
    case 'atleast': return `at least ${rule.n}`
    case 'exactly': return `pick ${rule.n}`
    case 'between': return `${rule.n}–${rule.m}`
  }
}

/** The plain-English line under the sentence: what the till will do. */
export function rulePlain(kind: VariantKind, rule: VariantRule, choiceCount: number): string {
  if (kind === 'size') {
    return `The till will not add the item until a size is chosen. ${choiceCount} size${choiceCount === 1 ? '' : 's'}.`
  }
  switch (rule.mode) {
    case 'optional': return 'Optional. The cashier can skip this.'
    case 'required': return 'Required. The till will not add the item until one choice is picked.'
    case 'any': return 'Optional. The cashier can skip this or tick as many as they like.'
    case 'upto': return `Optional, up to ${rule.m}. Extra taps are ignored.`
    case 'atleast': return `Required. At least ${rule.n} ${rule.n === 1 ? 'choice' : 'choices'}, no upper limit.`
    case 'exactly': return `Required. The till will not add the item until ${rule.n === 1 ? 'one choice is' : `${rule.n} choices are`} picked.`
    case 'between': return `Required. Between ${rule.n} and ${rule.m} choices.`
  }
}

export function draftFromGroup(g: ModifierGroup): VariantDraft {
  return {
    id: g.id,
    name: g.name,
    prompt: g.prompt,
    kind: kindOf(g),
    rule: ruleOf(g),
    allowQuantity: g.allow_quantity,
    maxPerOption: g.max_per_option,
    isActive: g.is_active,
    sortOrder: g.sort_order,
    choices: (g.options ?? []).map(choiceFromOption),
  }
}

export function choiceFromOption(o: ModifierOption): ChoiceDraft {
  return {
    id: o.id,
    name: o.name,
    price: o.price_delta,
    isDefault: o.is_default,
    isAvailable: o.is_available,
    groupRules: o.group_rules ?? [],
  }
}

export function emptyChoice(): ChoiceDraft {
  return { name: '', price: 0, isDefault: false, isAvailable: true, groupRules: [] }
}

export function emptyDraft(kind: VariantKind, sortOrder = 0): VariantDraft {
  return {
    name: '',
    prompt: '',
    kind,
    rule: defaultRule(kind),
    allowQuantity: false,
    maxPerOption: 1,
    isActive: true,
    sortOrder,
    choices: [emptyChoice(), emptyChoice()],
  }
}

/** "Which milk?" from "Milk", so the owner rarely has to type the prompt. */
export function promptFor(name: string, kind: VariantKind): string {
  const n = name.trim()
  if (!n) return ''
  if (kind === 'size') return 'Which size?'
  if (kind === 'many') return `Any ${n.toLowerCase()}?`
  return `Which ${n.toLowerCase()}?`
}

/** The size list an item gets when it stops sharing: named after the item. */
export function ownSizeListName(productName: string): string {
  const n = productName.trim() || 'Item'
  return `${n} sizes`
}

/**
 * Project a draft onto the wire shape, applying the constraints the DB will
 * enforce anyway, so the form cannot express a state the server rejects.
 */
export function groupPayload(d: VariantDraft): Partial<ModifierGroup> {
  const base = {
    name: d.name.trim(),
    prompt: d.prompt.trim() || promptFor(d.name, d.kind),
    is_active: d.isActive,
    sort_order: d.sortOrder,
  }
  if (d.kind === 'size') {
    return {
      ...base,
      selection_type: 'single',
      price_mode: 'replace',
      min_select: 1,
      max_select: 1,
      allow_quantity: false,
      max_per_option: 1,
    }
  }
  if (d.kind === 'one') {
    return {
      ...base,
      selection_type: 'single',
      price_mode: 'add',
      min_select: d.rule.mode === 'required' ? 1 : 0,
      max_select: 1,
      allow_quantity: false,
      max_per_option: 1,
    }
  }
  return {
    ...base,
    selection_type: 'multi',
    price_mode: 'add',
    min_select: Math.max(0, d.rule.n),
    max_select: d.rule.m,
    allow_quantity: d.allowQuantity,
    max_per_option: d.allowQuantity ? Math.max(1, d.maxPerOption) : 1,
  }
}

/** Rows for PUT /modifier-groups/:id/options, blank rows dropped. */
export function optionsPayload(d: VariantDraft) {
  return d.choices
    .filter((c) => c.name.trim() !== '')
    .map((c) => ({
      id: c.id,
      name: c.name.trim(),
      price_delta: Math.max(0, Number(c.price) || 0),
      is_available: c.isAvailable,
      // Only one default makes sense on a single-pick group; keep the first.
      is_default: c.isDefault,
      group_rules: c.groupRules,
    }))
}

/** Why a draft cannot be saved yet, or null. */
export function draftProblem(d: VariantDraft): string | null {
  if (d.name.trim().length < 2) return 'Give it a name.'
  const named = d.choices.filter((c) => c.name.trim() !== '')
  if (named.length === 0) return 'Add at least one choice.'
  if (d.kind === 'size' && named.length < 2) return 'Sizes need at least two choices, or use one price.'
  const lower = new Set<string>()
  for (const c of named) {
    const k = c.name.trim().toLowerCase()
    if (lower.has(k)) return `Two choices are called “${c.name.trim()}”.`
    lower.add(k)
    if (Number(c.price) < 0) return 'A price cannot be negative.'
  }
  if (d.kind === 'many' && d.rule.m != null && d.rule.n > d.rule.m) return 'The minimum is above the maximum.'
  return null
}

/** A one-line summary of the choices for list rows: "Regular ₨550 · Large ₨650". */
export function choicesSummary(
  g: Pick<ModifierGroup, 'price_mode' | 'options'>,
  money: (n: number) => string,
  limit = 4,
): string {
  const opts = (g.options ?? []).slice(0, limit).map((o) => {
    if (g.price_mode === 'replace') return `${o.name} ${money(o.price_delta)}`
    return o.price_delta > 0 ? `${o.name} +${money(o.price_delta)}` : o.name
  })
  const rest = (g.options?.length ?? 0) - limit
  return rest > 0 ? `${opts.join(' · ')} +${rest}` : opts.join(' · ')
}

/**
 * How many products end up asking each group, from the POS catalogue's
 * already-resolved product → group ids. The matrix and the library both
 * show "Used on 14 drinks" from this; no second source to drift.
 */
export function usageCounts(config: Pick<ProductModifierConfig, 'product_groups'> | undefined): Map<string, number> {
  const out = new Map<string, number>()
  if (!config) return out
  for (const ids of Object.values(config.product_groups)) {
    for (const id of ids) out.set(id, (out.get(id) ?? 0) + 1)
  }
  return out
}

/** Cashier-facing price for a choice: the item price for sizes, a delta otherwise. */
export function choicePriceLabel(kind: VariantKind, price: number, money: (n: number) => string): string {
  if (kind === 'size') return money(price)
  return price > 0 ? `+${money(price)}` : 'free'
}
